// src/stores/useLastSessionStore.js
//
// Persists a lightweight snapshot of the last active map session so
// IntentSelectorPage can offer a "Resume" shortcut.
//
// Shape:  { projectId: string | null, product: string | null }
//   • projectId  — useProjectStore activeProjectId ("global" counts as null)
//   • product    — the $tab route param used in pushView (e.g. "siteid",
//                  "padrekko", "well_finder", "map", "helios", "purchase")
//
// Written by MapPage whenever the user is on the map with a non-trivial
// session (product !== null).  Read by IntentSelectorPage to render the
// Resume card.
//
// Deliberately separate from useProjectStore (stable persist contract)
// and useAppViewStore (intentionally transient).

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { PERSIST_KEYS } from '../auth/persistKeys'

const STORAGE_KEY = PERSIST_KEYS.lastSession
const NON_RESUMABLE_TABS = new Set(["admin", "projects", "ops"]);


export const useLastSessionStore = create(
  persist(
    (set, get) => ({
      /* -------------------------------------------------- */
      /* STATE                                              */
      /* -------------------------------------------------- */

      projectId: null,   // string | null  ("global" is normalised → null)
      product:   null,   // string | null  (route $tab value)

      /* -------------------------------------------------- */
      /* ACTIONS                                            */
      /* -------------------------------------------------- */

      /**
       * Snapshot the current session.  Call this from MapPage whenever
       * projectId or activeTab changes.
       *
       * @param {string|null} projectId  — raw value from useProjectStore
       * @param {string|null} product    — activeTab from useParams
       */
      
      snapshotSession(projectId, product) {
        // Treat "global" the same as null — a global session isn't resumable
        // in a meaningful way (no specific project context to restore).
        const resolvedProject =
          !projectId || projectId === 'global' ? null : projectId

        // Only persist if at least the product is known.
        if (!product) return
        if (NON_RESUMABLE_TABS.has(product)) return

        const current = get()
        if (
          current.projectId === resolvedProject &&
          current.product    === product
        ) {
          return // nothing changed — skip the write
        }

        set({ projectId: resolvedProject, product })
      },

      /** Wipe the snapshot (e.g. on logout). */
      clearSession() {
        set({ projectId: null, product: null })
      },
    }),
    {
      name: STORAGE_KEY,
      version: 2,
      migrate: () => ({ projectId: null, product: null }),
      // Persist everything — the shape is intentionally small.
      partialize: (state) => ({
        projectId: state.projectId,
        product:   state.product,
      }),
    }
  )
)
